import NoData from '@/components/common/NoData'
import TableFooter from '@/components/common/Table/TableFooter'
import ButtonField from '@/components/form-control/ButtonField'
import { CharacterPlusIcon } from '@/components/icons'
import { flyInVariantsDashboard, SortBy } from '@/constant/common'
import { useAuthContext } from '@/features/auth/context'
import useSearchWaterSource from '@/features/config/hooks/watersource/useSearchWaterSource'
import { usePermission } from '@/hooks/permissions'
import { SearchParams } from '@/types'
import { usePagination } from '@mui/lab'
import { CircularProgress, SelectChangeEvent, Stack } from '@mui/material'
import { motion } from 'framer-motion'
import { debounce } from 'lodash'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useConfigUI } from '../../context'
import WaterSourceTable from './WaterSourceTable'
import classes from './style.module.scss'
import useTranslation from '@/hooks/useTranslation'

const MotionDiv = motion.div

const WaterSource = () => {
  const { openSidebar, setSidebarView } = useConfigUI()
  const { openModal, setModalView } = useConfigUI()

  const location = useLocation()
  const navigate = useNavigate()

  const { interfaceId } = useAuthContext()
  const { isViewOnly } = usePermission()
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useState<SearchParams>({
    PageSize: 10,
    PageIndex: 1,
  })
  const [currentPage, setCurrentPage] = useState(1)
  const { data, refetch } = useSearchWaterSource(searchParams, Number(interfaceId))

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const page = parseInt(event.target.value, 10)
    setCurrentPage(1)
    setSearchParams({
      ...searchParams,
      PageSize: page,
      PageIndex: 1,
    })
  }

  const handleAddWaterSource = () => {
    // ViewOnly users cannot add water source
    if (isViewOnly) return

    openSidebar()
    setSidebarView({ type: 'WATERSOURCE_VIEW' })
  }

  const handleSearch = debounce((event: SelectChangeEvent) => {
    const value = event.target.value.toString().trim()
    setCurrentPage(1)
    setSearchParams({
      ...searchParams,
      Keyword: value,
      PageIndex: 1,
    })
  }, 500)

  const { items } = usePagination({
    count: data?.totalPage,
    page: currentPage,
    onChange(_event: any, page: any) {
      setCurrentPage(page)
      setSearchParams({
        ...searchParams,
        PageIndex: page,
      })
    },
  })

  const handleDelete = (
    id: number | string,
    interfaceId: number | string,
    name: string,
  ) => {
    // ViewOnly users cannot delete water source
    if (isViewOnly) return

    openModal()
    setModalView({
      type: 'DELETE_WATER_SOURCE_VIEW',
      id: id.toString(),
      interfaceId: String(interfaceId),
      name,

      onSuccess: async () => {
        const newData = await refetch()
        if (newData.data?.list.length === 0 && currentPage !== 1) {
          setCurrentPage(currentPage - 1)
        }

        const newCurrentPage =
          newData.data?.list.length === 0 && currentPage !== 1
            ? currentPage - 1
            : currentPage

        setSearchParams({
          ...searchParams,
          PageIndex: newCurrentPage,
        })
      },
    })
  }

  const handleDesc = (sortBy: SortBy) => {
    setSearchParams({
      ...searchParams,
      SortBy: sortBy,
      SortDirection: 'desc',
    })

    const newSearchParams = {
      SortBy: sortBy,
      SortDirection: 'desc',
    }
    const combinedSearchParams = { ...newSearchParams }
    const queryString = new URLSearchParams(combinedSearchParams).toString()
    navigate(`?${queryString}`)

    return
  }

  const handleEsc = (sortBy: SortBy) => {
    setSearchParams({
      ...searchParams,
      SortBy: sortBy,
      SortDirection: 'asc',
    })

    const newSearchParams = {
      SortBy: sortBy,
      SortDirection: 'asc',
    }
    const combinedSearchParams = { ...newSearchParams }
    const queryString = new URLSearchParams(combinedSearchParams).toString()
    navigate(`?${queryString}`)
    return
  }

  useEffect(() => {
    const searchParamsFromURL = new URLSearchParams(location.search)

    // Extract values from URL and update searchParams
    const updatedSearchParams: SearchParams = {
      SortBy: searchParamsFromURL.get('SortBy') || undefined,
      SortDirection: searchParamsFromURL.get('SortDirection') || undefined,
      // Add other properties as needed
    }

    setSearchParams((prevSearchParams) => ({
      ...prevSearchParams,
      ...updatedSearchParams,
    }))
  }, [location.search])

  return (
    <Stack className={classes.wrapper}>
      <Stack flexGrow="1" height="100%">
        <Stack
          direction={'row'}
          justifyContent={'flex-end'}
          className={classes.header}
        >
          <ButtonField
            startIcon={<CharacterPlusIcon />}
            style={{
              height: '32px',
              minWidth: '89px',
              borderRadius: '5px',
            }}
            name={t('waterSourceAddNew')}
            onClick={handleAddWaterSource}
            disabled={isViewOnly}
            sx={{ opacity: isViewOnly ? 0.6 : 1 }}
          />
        </Stack>

        <MotionDiv
          initial="initial"
          animate="animate"
          variants={flyInVariantsDashboard}
          className={classes.content}
        >
          {!data && (
            <Stack className={classes.circleProgress}>
              <CircularProgress />
            </Stack>
          )}

          {data && data?.list.length > 0 && (
            <WaterSourceTable
              rows={data.list}
              onDelete={handleDelete}
              handleDesc={handleDesc}
              handleEsc={handleEsc}
            />
          )}

          {data && data.totalCount === 0 && (
            <NoData text={t('commonStatusNoData')} />
          )}
        </MotionDiv>

        <MotionDiv
          initial="initial"
          animate="animate"
          variants={flyInVariantsDashboard}
        >
          <Stack className={classes.footer}>
            <TableFooter
              page={data?.page}
              totalCount={data?.totalCount}
              totalPage={data?.totalPage}
              rowsPerPage={data?.size ?? 0}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              items={items}
            ></TableFooter>
          </Stack>
        </MotionDiv>
      </Stack>
    </Stack>
  )
}

export default WaterSource
